import { BareActionContext, getStoreBuilder } from 'vuex-typex';
import axios from 'axios';
import { RootState } from '@/store/state';
import { ErpState } from './state';
import appStore from '@/store/app';
import authenticator from '@/store/authentication';
import { Api, ApiResult, ResultOf, Statement, Amount, ErpOrder, ErpOrderLine, MarketAd, MarketAdType, Statistic, SelectItem, ProductInOrder, TypeOfSearch, SingleResultOf } from '@/types';
import { ErpOrdersSearch, MarketAdsSearch } from '@/models';

type StoreContext = BareActionContext<ErpState, RootState>;

const thisState: ErpState = {
   statements: [],
   totalStatement: 0,
   amounts: [],
   totalAmounts: 0,
   ordersSearch: new ErpOrdersSearch(),
   orders: [],
   totalOrders: 0,
   orderLines: [],
   totalOrderLines: 0,
   marketAds: [],
   totalMarketAds: 0,
   marketAdsSearch: new MarketAdsSearch(),
   productInOrders: [],
   totalProductInOrders: 0,
   statistics: [],
   totalStatistics: 0,
   marketTypes: [
      { value: MarketAdType[MarketAdType.RC], description: 'Ricerche' } as SelectItem<string>,
      { value: MarketAdType[MarketAdType.OF], description: 'Offerte' } as SelectItem<string>,
      { value: MarketAdType[MarketAdType.PR], description: 'Lavoro' } as SelectItem<string>,
      { value: MarketAdType[MarketAdType.VA], description: 'Varie' } as SelectItem<string>,
      { value: MarketAdType[MarketAdType.AL], description: 'Allarmi' } as SelectItem<string>,
   ],
};

const moduleBuilder = getStoreBuilder<RootState>().module('accounts', thisState);

const thisModule = {
   getters: {
      statements(state: ErpState): Statement[] {
         return state.statements;
      },

      totalStatement(state: ErpState): number {
         return state.totalStatement;
      },

      amounts(state: ErpState): Amount[] {
         return state.amounts;
      },

      totalAmounts(state: ErpState): number {
         return state.totalAmounts;
      },

      orders(state: ErpState): ErpOrder[] {
         return state.orders;
      },

      totalOrders(state: ErpState): number {
         return state.totalOrders;
      },

      orderLines(state: ErpState): ErpOrderLine[] {
         return state.orderLines;
      },

      totalOrderLines(state: ErpState): number {
         return state.totalOrderLines;
      },

      ordersSearch(state: ErpState): ErpOrdersSearch {
         return state.ordersSearch;
      },

      marketAds(state: ErpState): MarketAd[] {
         return state.marketAds;
      },

      totalMarketAds(state: ErpState): number {
         return state.totalMarketAds;
      },

      marketAdsSearch(state: ErpState): MarketAdsSearch {
         return state.marketAdsSearch;
      },

      marketTypes(state: ErpState): SelectItem<string>[] {
         return state.marketTypes;
      },

      productInOrders(state: ErpState): ProductInOrder[] {
         return state.productInOrders;
      },

      totalProductInOrders(state: ErpState): number {
         return state.totalProductInOrders;
      },

      statistics(state: ErpState): Statistic[] {
         return state.statistics;
      },

      totalStatistics(state: ErpState): number {
         return state.totalStatistics;
      },
   },

   actions: {
      async getStatementsAsync(context: StoreContext, payload: { year: number, page: number, size: number }): Promise<void> {

         if (!payload) { return; }

         appStore.loading({ isLoading: true });

         return await axios.get<ResultOf<Statement>>(Api.BaseUrl + `erp/statements/${payload.year}`, {
            params: {
               page: payload.page,
               size: payload.size,
            },
         })
            .then((response) => {
               commitPopulateStatements({ statements: response.data.payload, total: response.data.total });
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: true });
               }
               commitPopulateStatements({ statements: [], total: 0 });
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },

      async printStatementsAsync(context: StoreContext, payload: { year: number }): Promise<Blob | undefined> {

         if (!payload || payload.year === 0) {
            appStore.error({ message: 'Impossibile stampare. Il filtro per anno di stampa non è stato specificato.'});
            return;
         }

         appStore.loading({ isLoading: true });

         return await axios.get<Blob>(Api.BaseUrl + `erp/statements/print/${payload.year}`, {
            responseType: 'blob',
         })
         .then((response) => {
            return response.data;
         })
         .catch((reason) => {
            if (reason && reason.response) {
               appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: true });
            }
            return undefined;
         })
         .finally(() => {
            appStore.loading({ isLoading: false });
         });
      },

      async getAmountsAsync(context: StoreContext, payload: { month: number, year: number, page: number, size: number }): Promise<void> {

         if (!payload) { return; }

         appStore.loading({ isLoading: true });

         return await axios.get<ResultOf<Amount>>(Api.BaseUrl + `erp/amounts/${payload.month}/${payload.year}`, {
            params: {
               page: payload.page,
               size: payload.size,
            },
         })
            .then((response) => {
               commitPopulateAmounts({ amounts: response.data.payload, total: response.data.total });
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: true });
               }
               commitPopulateAmounts({ amounts: [], total: 0 });
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },

      async printAmountsAsync(context: StoreContext, payload: { month: number, year: number }): Promise<Blob | undefined> {

         if (!payload || payload.month === 0 || payload.year === 0) {
            appStore.error({ message: 'Impossibile stampare. Il filtro per mese / anno di stampa non è stato specificato.'});
            return;
         }

         appStore.loading({ isLoading: true });

         return await axios.get<Blob>(Api.BaseUrl + `erp/amounts/print/${payload.month}/${payload.year}`, {
            responseType: 'blob',
         })
         .then((response) => {
            return response.data;
         })
         .catch((reason) => {
            if (reason && reason.response) {
               appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: true });
            }
            return undefined;
         })
         .finally(() => {
            appStore.loading({ isLoading: false });
         });
      },

      async exportAmountsAsync(context: StoreContext, payload: { month: number, year: number, page: number, size: number }): Promise<Blob | undefined> {

         if (!payload) { return; }

         appStore.loading({ isLoading: true });

         return await axios.get<Blob>(Api.BaseUrl + `erp/amounts/export/${payload.month}/${payload.year}`, {
            responseType: 'blob',
            params: {
               page: payload.page,
               size: payload.size,
            },
         })
            .then((response) => {
               return response.data;
               // commitPopulateAmounts({ amounts: response.data.payload, total: response.data.total });
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: true });
               }
               return undefined;
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },

      async getOrdersAsync(context: StoreContext, payload: ErpOrdersSearch): Promise<void> {

         if (!payload) { return; }

         appStore.loading({ isLoading: true });

         return await axios.get<ResultOf<ErpOrder>>(Api.BaseUrl + `erp/orders/${payload.day}/${payload.month}/${payload.year}`, {
            params: {
               page: payload.pageNumber,
               size: payload.pageSize,
            },
         })
            .then((response) => {
               commitPopulateOrders({ orders: response.data.payload, total: response.data.total, search: payload });
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: true });
               }
               commitPopulateOrders({ orders: [], total: 0, search: payload });
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },

      async getOrderLinesAsync(context: StoreContext, payload: { day: number, month: number, year: number, documentNumber: number, orderBy: string }): Promise<void> {

         if (!payload) { return; }

         appStore.loading({ isLoading: true });

         return await axios.get<ResultOf<ErpOrderLine>>(Api.BaseUrl + `erp/orders/${payload.documentNumber}/lines/${payload.day}/${payload.month}/${payload.year}`, {
            params: {
               sort: payload.orderBy,
            },
         })
            .then((response) => {
               commitPopulateOrderLines({ orderLines: response.data.payload, total: response.data.total });
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: true });
               }
               commitPopulateOrderLines({ orderLines: [], total: 0 });
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },

      async getMarketAdsAsync(context: StoreContext, payload: MarketAdsSearch): Promise<void> {

         if (!payload) { return; }

         appStore.loading({ isLoading: true });

         const keywordSearch = payload.keyword === '' ? '*' : payload.keyword;

         return await axios.get<ResultOf<MarketAd>>(Api.BaseUrl + `erp/marketAds/search/${keywordSearch}/${payload.adType}`, {
            params: {
               page: payload.pageNumber,
               size: payload.pageSize,
            },
         })
            .then((response) => {
               commitPopulateMarketAds({ marketAds: response.data.payload, total: response.data.total, search: payload });
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: true });
               }
               commitPopulateMarketAds({ marketAds: [], total: 0, search: payload });
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },

      async upsertMarketAdAsync(context: StoreContext, payload: {
         id: number, title: string, message?: string,
         partnerName: string, type: string, createdAt: Date, expiryDate: Date
      }): Promise<boolean> {

         if (!payload) { return false; }

         appStore.loading({ isLoading: true });

         const isNew = payload.id === 0;

         return await axios.post<SingleResultOf<number>>(Api.BaseUrl + `erp/marketAds`, payload)
            .then((response) => {
               if (isNew) {
                  payload.id = response.data.payload || 0;
                  commitInsertMarketAd(payload);
               } else {
                  commitUpdateMarketAd(payload);
               }
               return true;
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: false });
               }
               return false;
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },

      async deleteMarketAdAsync(context: StoreContext, payload: { id: number }): Promise<boolean> {

         if (!payload) { return false; }

         appStore.loading({ isLoading: true });

         return await axios.delete(Api.BaseUrl + `erp/marketAds/${payload.id}`)
            .then((result) => {
               commitDeleteMarketAd(payload);
               appStore.success({ message: 'L\'annuncio è stato eliminato con successo.' });
               return true;
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: false });
               }
               return false;
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },

      async getProductsInOrdersAsync(context: StoreContext, payload: { keyword: string, searchType: TypeOfSearch, searchAll: boolean, page: number, size: number }): Promise<void> {

         if (!payload) { return; }

         appStore.loading({ isLoading: true });

         const keywordSearch = payload.keyword === '' ? '*' : payload.keyword;

         return await axios.get<ResultOf<ProductInOrder>>(Api.BaseUrl + `erp/orders/products/${keywordSearch}/${payload.searchType}/${payload.searchAll}`, {
            params: {
               page: payload.page,
               size: payload.size,
            },
         })
            .then((response) => {
               commitPopulateProductsInOrders({ products: response.data.payload, total: response.data.total });
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: true });
               }
               commitPopulateProductsInOrders({ products: [], total: 0 });
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },

      async getStatisticsAsync(context: StoreContext, payload: { fan: string, page: number, size: number, sortField: string, sortDirection: string }): Promise<void> {

         if (!payload) { return; }

         appStore.loading({ isLoading: true });

         return await axios.get<ResultOf<Statistic>>(Api.BaseUrl + `erp/statistics/${payload.fan}`, {
            params: {
               page: payload.page,
               size: payload.size,
               sortField: payload.sortField,
               sortDirection: payload.sortDirection,
            },
         })
            .then((response) => {
               commitPopulateStatistics({ statistics: response.data.payload, total: response.data.total });
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: true });
               }
               commitPopulateStatistics({ statistics: [], total: 0 });
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },
   },

   mutations: {

      populateStatements(state: ErpState, payload: { statements: Statement[], total: number }) {
         state.statements = payload.statements;
         state.totalStatement = payload.total;
      },

      populateAmounts(state: ErpState, payload: { amounts: Amount[], total: number }) {
         state.amounts = payload.amounts;
         state.totalAmounts = payload.total;
      },

      populateOrders(state: ErpState, payload: { orders: ErpOrder[], total: number, search: ErpOrdersSearch }) {
         state.orders = payload.orders;
         state.totalOrders = payload.total;
         state.ordersSearch = payload.search;
      },

      populateOrderLines(state: ErpState, payload: { orderLines: ErpOrderLine[], total: number }) {
         state.orderLines = payload.orderLines;
         state.totalOrderLines = payload.total;
      },

      populateMarketAds(state: ErpState, payload: { marketAds: MarketAd[], total: number, search: MarketAdsSearch }) {
         state.marketAds = payload.marketAds;
         state.totalMarketAds = payload.total;
         state.marketAdsSearch = payload.search;
      },

      insertMarketAd(state: ErpState, payload: {
         id: number, title: string, message?: string,
         partnerName: string, type: string, createdAt: Date, expiryDate: Date
      }) {

         const typeDescription = state.marketTypes.filter((mk) => mk.value === payload.type)[0].description;

         const partnerCode = authenticator.userProfile.aid;

         const marketAdd: MarketAd = {
            id: payload.id,
            title: payload.title,
            message: payload.message || '',
            createdAt: payload.createdAt,
            expiryDate: payload.expiryDate,
            partnerCode,
            partnerName: payload.partnerName,
            type: payload.type,
            typeDescription,
         };
         state.marketAds.unshift(marketAdd);
         state.totalMarketAds++;
      },

      updateMarketAd(state: ErpState, payload: {
         id: number, title: string, message?: string,
         partnerName: string, type: string, createdAt: Date, expiryDate: Date
      }) {
         const index = state.marketAds.findIndex((x) => x.id === payload.id);
         if (index > -1) {
            const typeDescription = state.marketTypes.filter((mk) => mk.value === payload.type)[0].description;

            const updated = state.marketAds[index];
            updated.type = payload.type;
            updated.typeDescription = typeDescription;
            updated.title = payload.title;
            updated.message = payload.message || '';
            state.marketAds.splice(index, 1, updated);
         }
      },

      deleteMarketAd(state: ErpState, payload: { id: number }) {
         if (state.marketAds) {
            const index = state.marketAds.findIndex((marketAd) => marketAd.id === payload.id);
            if (index > -1) {
               state.marketAds.splice(index, 1);
            }
         }
         if (state.totalMarketAds > 0) {
            state.totalMarketAds--;
         }
      },

      populateProductsInOrders(state: ErpState, payload: { products: ProductInOrder[], total: number }) {
         state.productInOrders = payload.products;
         state.totalProductInOrders = payload.total;
      },

      populateStatistics(state: ErpState, payload: { statistics: Statistic[], total: number }) {
         state.statistics = payload.statistics;
         state.totalStatistics = payload.total;
      },
   },

};

// private getters
const getStatements = moduleBuilder.read(thisModule.getters.statements, 'getStatements');
const getTotalStatements = moduleBuilder.read(thisModule.getters.totalStatement, 'getTotalStatement');
const getAmounts = moduleBuilder.read(thisModule.getters.amounts, 'getAmounts');
const getTotalAmounts = moduleBuilder.read(thisModule.getters.totalAmounts, 'getTotalAmounts');
const getOrders = moduleBuilder.read(thisModule.getters.orders, 'getOrders');
const getTotalOrders = moduleBuilder.read(thisModule.getters.totalOrders, 'getTotalOrders');
const getOrdersSearch = moduleBuilder.read(thisModule.getters.ordersSearch, 'getOrdersSearch');
const getOrderLines = moduleBuilder.read(thisModule.getters.orderLines, 'getOrderLines');
const getTotalOrderLines = moduleBuilder.read(thisModule.getters.totalOrderLines, 'getTotalOrderLines');
const getMarketAds = moduleBuilder.read(thisModule.getters.marketAds, 'getMarketAds');
const getTotalMarketAds = moduleBuilder.read(thisModule.getters.totalMarketAds, 'getTotalMarketAds');
const getMarketAdsSearch = moduleBuilder.read(thisModule.getters.marketAdsSearch, 'getMarketAdsSearch');
const getMarketTypes = moduleBuilder.read(thisModule.getters.marketTypes, 'getMarketTypes');
const getProductInOrders = moduleBuilder.read(thisModule.getters.productInOrders, 'getProductInOrders');
const getTotalProductInOrders = moduleBuilder.read(thisModule.getters.totalProductInOrders, 'getTotalProductInOrders');
const getStatistics = moduleBuilder.read(thisModule.getters.statistics, 'getStatistics');
const getTotalStatistics = moduleBuilder.read(thisModule.getters.totalStatistics, 'getTotalStatistics');

// mutations
const commitPopulateStatements = moduleBuilder.commit(thisModule.mutations.populateStatements, 'populateStatements');
const commitPopulateAmounts = moduleBuilder.commit(thisModule.mutations.populateAmounts, 'populateAmounts');
const commitPopulateOrders = moduleBuilder.commit(thisModule.mutations.populateOrders, 'populateOrders');
const commitPopulateOrderLines = moduleBuilder.commit(thisModule.mutations.populateOrderLines, 'populateOrderLines');
const commitPopulateMarketAds = moduleBuilder.commit(thisModule.mutations.populateMarketAds, 'populateMarketAds');
const commitDeleteMarketAd = moduleBuilder.commit(thisModule.mutations.deleteMarketAd, 'deleteMarketAd');
const commitUpdateMarketAd = moduleBuilder.commit(thisModule.mutations.updateMarketAd, 'updateMarketAd');
const commitInsertMarketAd = moduleBuilder.commit(thisModule.mutations.insertMarketAd, 'insertMarketAd');
const commitPopulateProductsInOrders = moduleBuilder.commit(thisModule.mutations.populateProductsInOrders, 'populateProductsInOrders');
const commitPopulateStatistics = moduleBuilder.commit(thisModule.mutations.populateStatistics, 'populateStatistics');

const erp = {

   // getters (wrapped as real getters)
   get statements() { return getStatements(); },
   get totalStatement() { return getTotalStatements(); },
   get amounts() { return getAmounts(); },
   get totalAmounts() { return getTotalAmounts(); },
   get orders() { return getOrders(); },
   get totalOrders() { return getTotalOrders(); },
   get ordersSearch() { return getOrdersSearch(); },
   get orderLines() { return getOrderLines(); },
   get totalOrderLines() { return getTotalOrderLines(); },
   get marketAds() { return getMarketAds(); },
   get totalMarketAds() { return getTotalMarketAds(); },
   get marketAdsSearch() { return getMarketAdsSearch(); },
   get marketTypes() { return getMarketTypes(); },
   get productInOrders() { return getProductInOrders(); },
   get totalProductInOrders() { return getTotalProductInOrders(); },
   get statistics() { return getStatistics(); },
   get totalStatistics() { return getTotalStatistics(); },

   // actions
   fetchStatementsAsync: moduleBuilder.dispatch(thisModule.actions.getStatementsAsync, 'getStatementsAsync'),
   printStatementsAsync: moduleBuilder.dispatch(thisModule.actions.printStatementsAsync, 'printStatementsAsync'),
   fetchAmountsAsync: moduleBuilder.dispatch(thisModule.actions.getAmountsAsync, 'getAmountsAsync'),
   printAmountsAsync: moduleBuilder.dispatch(thisModule.actions.printAmountsAsync, 'printAmountsAsync'),
   exportAmountsAsync: moduleBuilder.dispatch(thisModule.actions.exportAmountsAsync, 'exportAmountsAsync'),
   fetchOrdersAsync: moduleBuilder.dispatch(thisModule.actions.getOrdersAsync, 'getOrdersAsync'),
   fetchOrderLinesAsync: moduleBuilder.dispatch(thisModule.actions.getOrderLinesAsync, 'getOrderLinesAsync'),
   fetchMarketAdsAsync: moduleBuilder.dispatch(thisModule.actions.getMarketAdsAsync, 'getMarketAdsAsync'),
   upsertMarketAdAsync: moduleBuilder.dispatch(thisModule.actions.upsertMarketAdAsync, 'upsertMarketAdAsync'),
   deleteMarketAdAsync: moduleBuilder.dispatch(thisModule.actions.deleteMarketAdAsync, 'deleteMarketAdAsync'),
   fetchProductsInOrdersAsync: moduleBuilder.dispatch(thisModule.actions.getProductsInOrdersAsync, 'getProductsInOrdersAsync'),
   fetchStatisticsAsync: moduleBuilder.dispatch(thisModule.actions.getStatisticsAsync, 'getStatisticsAsync'),
};

export default erp;
